/**
 * Bundle Picker — bettinails.nl
 * Custom element that handles the 3-slot bundle builder UI.
 * Collections used:
 *   - plaknagels-kort   (nail sets, short)
 *   - plaknagels-medium (nail sets, medium)
 *   - plaknagels-accessoires (accessories)
 *
 * Cart strategy: adds 3 separate line items each with
 *   _bundle_id: "bundle_{timestamp}" property.
 *   The underscore prefix hides the property in Shopify's cart UI.
 */

class BundlePickerComponent extends HTMLElement {
  connectedCallback() {
    this.blockId      = this.dataset.blockId;
    this.drawer       = document.getElementById(`BundleDrawer-${this.blockId}`);
    this.drawerGrid   = document.getElementById(`BundleDrawerGrid-${this.blockId}`);
    this.detailDrawer = document.getElementById(`BundleDetailsDrawer-${this.blockId}`);

    // Move drawers to <body> so they escape the Shopify section's stacking context.
    // Without this, Savor's sticky nav (in a different stacking context) always
    // paints over the drawer regardless of z-index.
    document.body.appendChild(this.drawer);
    document.body.appendChild(this.detailDrawer);

    // Slot count read from DOM — works for 2, 3 or 4 slot variants
    const slotEls   = this.querySelectorAll('.bundle-picker__slot');
    this.slots      = Array.from(slotEls).map(() => null);
    this.activeSlot = null;
    this.tempSelection = null;
    this._detailProduct    = null;  // ALWAYS the products.json object (price in decimals) — never overwritten with /products/handle.js data
    this._detailMedia      = [];     // normalised [{type:'image'|'video', ...}] used only for display
    this._detailMediaIndex = 0;
    this._detailFetchToken = 0;      // guards against out-of-order media fetches
    this._gridFetchToken   = 0;      // guards against out-of-order grid renders (see _openDrawer)

    // Bundle name + price read from data attributes on the component element
    this._bundleName          = this.dataset.bundleName         || 'Betti Bundel';
    this._bundlePrice         = this.dataset.bundlePrice        || '€49,95';
    this._bundleOriginalPrice = this.dataset.bundleOriginalPrice || '';
    this._bundlePriceCents    = parseInt(this.dataset.bundlePriceCents || '0', 10)
                                || this._parseMoney(this._bundlePrice);

    // Product cache keyed by comma-joined collection handles
    this._cache = {};

    this._bindSlots();
    this._bindDrawer();
    this._bindDetailsDrawer();
  }

  /* ---- Event binding ---- */

  _bindSlots() {
    this.querySelectorAll('.bundle-picker__slot').forEach(slotEl => {
      slotEl.addEventListener('click', e => {
        // If the clear button was clicked, clear the slot (don't open drawer)
        if (e.target.closest('.bundle-picker__slot-clear')) {
          e.stopPropagation();
          this._clearSlot(parseInt(slotEl.dataset.slot));
          return;
        }
        this._openDrawer(parseInt(slotEl.dataset.slot), slotEl.dataset.slotType);
      });
    });

    this.querySelector('.bundle-picker__atc').addEventListener('click', () => {
      this._addToCart();
    });
  }

  _bindDrawer() {
    this.drawer.querySelector('.bundle-drawer__close').addEventListener('click', () => this._closeDrawer());
    this.drawer.querySelector('.bundle-drawer__overlay').addEventListener('click', () => this._closeDrawer());
    this.drawer.querySelector('.bundle-drawer__confirm').addEventListener('click', () => this._confirmSelection());

    // Trap Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') {
        this._closeDrawer();
      }
    });
  }

  _bindDetailsDrawer() {
    const dd = this.detailDrawer;

    dd.querySelector('.bundle-details-drawer__close').addEventListener('click', () => this._closeDetails());
    dd.querySelector('.bundle-details-drawer__select').addEventListener('click', () => {
      if (this._detailProduct) {
        this._selectProductData(this._detailProduct);
        this._confirmSelection();
        this._closeDetails();
      }
    });

    // Swipe support on main display — images and videos alike. Videos have no
    // native controls (they loop muted like a GIF), so the whole surface swipes.
    const mainWrap = dd.querySelector('.bundle-details-drawer__main-wrap');
    let touchStartX = 0;
    let swipeActive = false;
    mainWrap.addEventListener('touchstart', e => {
      swipeActive = true;
      touchStartX = e.touches[0].clientX;
      this._hideSwipeHint(); // first interaction dismisses (and remembers) the hint
    }, { passive: true });
    mainWrap.addEventListener('touchend', e => {
      if (!swipeActive) return;
      swipeActive = false;
      const diff  = touchStartX - e.changedTouches[0].clientX;
      const count = this._detailMedia?.length || 0;
      if (count && Math.abs(diff) > 40) {
        if (diff > 0) {
          this._showDetailMedia(Math.min(this._detailMediaIndex + 1, count - 1));
        } else {
          this._showDetailMedia(Math.max(this._detailMediaIndex - 1, 0));
        }
      }
    }, { passive: true });
  }

  /* ---- Drawer ---- */

  _openDrawer(slotIndex, slotType) {
    this.activeSlot    = slotIndex;
    this.activeType    = slotType;
    this.tempSelection = this.slots[slotIndex]; // pre-select current product if any

    // Update drawer title
    const title = slotType === 'nails' ? 'Kies een nagelset' : 'Kies een accessoire';
    this.drawer.querySelector('.bundle-drawer__title').textContent = title;

    // Reset confirm button
    const confirmBtn = this.drawer.querySelector('.bundle-drawer__confirm');
    confirmBtn.disabled = true;
    this.drawer.querySelector('.bundle-drawer__status').textContent = '';

    // Open
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Load + render products
    const handles = slotType === 'nails'
      ? [this.dataset.collectionNailsKort, this.dataset.collectionNailsMedium]
      : [this.dataset.collectionAccessoires];

    // Guard against out-of-order responses. Both slot types render into the same
    // shared drawerGrid, so a slow 'nails' fetch resolving after the user has
    // reopened on 'accessory' would paint nail sets into the accessory drawer —
    // producing a cart that fails Bundler's section rules. Mirrors the existing
    // _detailFetchToken pattern used by _openDetails.
    const token = ++this._gridFetchToken;

    this._loadProducts(handles).then(products => {
      if (token !== this._gridFetchToken) return; // a newer open superseded this one
      this._renderGrid(products);
    });
  }

  _closeDrawer() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.activeSlot    = null;
    this.tempSelection = null;
  }

  _confirmSelection() {
    if (this.tempSelection !== null && this.activeSlot !== null) {
      this._setSlot(this.activeSlot, this.tempSelection);
      this._closeDrawer();
    }
  }

  /* ---- Product loading ---- */

  async _loadProducts(handles) {
    const cacheKey = handles.join(',');
    if (this._cache[cacheKey]) return this._cache[cacheKey];

    this.drawerGrid.innerHTML = '<div class="bundle-drawer__loading">Producten laden…</div>';

    // Shopify caps products.json at 250 per page. Previously this fetched a single
    // page of 50, silently hiding every product past the 50th in a collection.
    // Page through until a short page comes back, with a hard stop as a safety net.
    const PAGE_SIZE  = 250;
    const MAX_PAGES  = 8;

    const allProducts = [];
    for (const handle of handles) {
      try {
        for (let page = 1; page <= MAX_PAGES; page++) {
          const res = await fetch(`/collections/${handle}/products.json?limit=${PAGE_SIZE}&page=${page}`);
          if (!res.ok) break;
          const data  = await res.json();
          const batch = data.products || [];
          allProducts.push(...batch);
          if (batch.length < PAGE_SIZE) break; // last page reached
        }
      } catch (err) {
        console.error(`[BundlePicker] Failed to load collection "${handle}":`, err);
      }
    }

    // Deduplicate by product ID (a product may appear in multiple collections)
    const seen    = new Set();
    const unique  = allProducts.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });

    this._cache[cacheKey] = unique;
    return unique;
  }

  /* ---- Variant selection ---- */

  /**
   * Returns the variant that should be added to the cart for a product.
   * Prefers the first available variant; falls back to the first variant so a
   * fully sold-out product still renders (flagged as unavailable) rather than
   * disappearing. Returns null when the product has no variants at all, which
   * callers must treat as unselectable.
   */
  _pickVariant(product) {
    const variants = product?.variants;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    return variants.find(v => v.available) || variants[0];
  }

  /* ---- Grid rendering ---- */

  _renderGrid(products) {
    if (!products.length) {
      this.drawerGrid.innerHTML = '<div class="bundle-drawer__loading">Geen producten gevonden.</div>';
      return;
    }

    const selectedId = this.tempSelection?.id ?? null;

    this.drawerGrid.innerHTML = products.map(p => {
      // Pick the variant the customer will actually receive. Previously this was
      // always variants[0] while availability was computed with .some(), so a
      // product could render as in-stock while the variant added to the cart was
      // sold out (or not the one registered in the Bundler bundle).
      const variant     = this._pickVariant(p);
      const imgSrc      = this._productImage(p);
      const isSelected  = selectedId === p.id;
      const isAvailable = !!variant?.available;

      return `
        <div
          class="bundle-product-card${isSelected ? ' bundle-product-card--selected' : ''}${!isAvailable ? ' bundle-product-card--soldout' : ''}"
          data-product-id="${p.id}"
          data-variant-id="${variant?.id ?? ''}"
          data-title="${this._esc(p.title)}"
          data-image="${imgSrc}"
          data-price="${parseFloat(variant?.price || '0')}"
          data-available="${isAvailable}"
          aria-pressed="${isSelected}"
          ${!isAvailable ? 'aria-disabled="true"' : ''}
        >
          <div class="bundle-product-card__image-wrap">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this._esc(p.title)}" width="150" height="150" loading="lazy">` : ''}
            <span class="bundle-product-card__check" aria-hidden="true">✓</span>
            ${!isAvailable ? '<span class="bundle-product-card__soldout-label">Uitverkocht</span>' : ''}
          </div>
          <span class="bundle-product-card__name">${this._esc(p.title)}</span>
          <button
            class="bundle-product-card__details"
            type="button"
            data-details-id="${p.id}"
            aria-label="Bekijk foto's van ${this._esc(p.title)}"
          >Bekijk foto's</button>
        </div>
      `;
    }).join('');

    // Clicking the card (but not the Details button) selects the product
    this.drawerGrid.querySelectorAll('.bundle-product-card').forEach(card => {
      card.addEventListener('click', e => {
        if (e.target.closest('.bundle-product-card__details')) return;
        if (card.dataset.available === 'false') return; // block sold-out
        this._selectProduct(card);
      });
    });

    // Clicking Details opens the details drawer
    this.drawerGrid.querySelectorAll('.bundle-product-card__details').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const productId = parseInt(btn.dataset.detailsId, 10);
        const product   = products.find(p => p.id === productId);
        if (product) this._openDetails(product);
      });
    });

    this._updateConfirmButton();
  }

  _productImage(product) {
    const src = product.images?.[0]?.src;
    if (!src) return '';
    // Request a 300px version from Shopify CDN
    return src.replace(/(\.[^.?]+)(\?|$)/, '_300x300$1$2');
  }

  _esc(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---- Selection ---- */

  _selectProduct(card) {
    this.drawerGrid.querySelectorAll('.bundle-product-card').forEach(c => {
      c.classList.remove('bundle-product-card--selected');
      c.setAttribute('aria-pressed', 'false');
    });

    card.classList.add('bundle-product-card--selected');
    card.setAttribute('aria-pressed', 'true');

    this.tempSelection = {
      id:        parseInt(card.dataset.productId, 10),
      variantId: parseInt(card.dataset.variantId, 10),
      title:     card.dataset.title,
      image:     card.dataset.image,
      price:     parseFloat(card.dataset.price),
    };

    this._updateConfirmButton();
  }

  _selectProductData(product) {
    // Must match _renderGrid's choice, otherwise selecting via the details drawer
    // would add a different variant than the one the card advertised.
    const variant = this._pickVariant(product);
    this.tempSelection = {
      id:        product.id,
      variantId: parseInt(variant?.id || 0, 10),
      title:     product.title,
      image:     this._productImage(product),
      price:     parseFloat(variant?.price || '0'),
    };
    // Also visually mark the card as selected in the grid
    this.drawerGrid.querySelectorAll('.bundle-product-card').forEach(c => {
      const isThis = parseInt(c.dataset.productId, 10) === product.id;
      c.classList.toggle('bundle-product-card--selected', isThis);
      c.setAttribute('aria-pressed', String(isThis));
    });
    this._updateConfirmButton();
  }

  _openDetails(product) {
    this._detailProduct    = product;   // products.json object — kept for selection/price
    this._detailMediaIndex = 0;

    const dd = this.detailDrawer;
    dd.querySelector('.bundle-details-drawer__title').textContent = product.title;

    // Disable select button if product is sold out
    const isAvailable = product.variants?.some(v => v.available) ?? false;
    const selectBtn = dd.querySelector('.bundle-details-drawer__select');
    selectBtn.disabled = !isAvailable;
    selectBtn.textContent = isAvailable ? 'Selecteer' : 'Uitverkocht';

    // 1) Render immediately from products.json images — no perceived delay and this
    //    is the exact prior behaviour (images only) used as the guaranteed fallback.
    const images = product.images?.length ? product.images : [{ src: this._productImage(product) }];
    this._detailMedia = this._mediaFromImages(images);
    this._buildDetailControls();
    this._renderDetailThumbs();
    this._showDetailMedia(0);

    dd.setAttribute('aria-hidden', 'false');

    // 2) Fetch full media (incl. native videos) in the background. Guarded by a token
    //    so a slow fetch for a previously-opened product can never overwrite a newer one.
    //    NOTE: this data is used for DISPLAY ONLY. Prices in /products/{handle}.js are in
    //    cents — never feed any of it into selection (_detailProduct stays products.json).
    const token = ++this._detailFetchToken;
    if (product.handle) {
      fetch(`/products/${encodeURIComponent(product.handle)}.js`)
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (!data || token !== this._detailFetchToken) return;       // superseded / drawer changed
          if (!this._detailProduct || this._detailProduct.id !== product.id) return;
          const media = this._mediaFromJs(data.media || []);
          if (!media.some(m => m.type === 'video')) return;             // no video → keep image-only view untouched
          this._detailMedia = media;
          const keep = Math.min(this._detailMediaIndex, media.length - 1);
          this._buildDetailControls();
          this._renderDetailThumbs();
          this._showDetailMedia(keep < 0 ? 0 : keep);
        })
        .catch(err => {
          // Network/parse failure — images are already displayed, so degrade silently.
          console.error('[BundlePicker] media fetch failed:', err);
        });
    }
  }

  _closeDetails() {
    this._teardownDetailVideo();
    const wrap    = this.detailDrawer.querySelector('.bundle-details-drawer__main-wrap');
    const mainImg = this.detailDrawer.querySelector('.bundle-details-drawer__main-img');
    this._clearSlideLayer(wrap, mainImg);      // drop any in-flight slide layer/transform
    if (wrap) wrap.classList.remove('show-swipe-hint');
    clearTimeout(this._swipeHintTimer);
    if (mainImg) mainImg.style.display = '';   // restore default visible image element

    this.detailDrawer.setAttribute('aria-hidden', 'true');
    this._detailProduct    = null;
    this._detailMedia      = [];
    this._detailMediaIndex = 0;
    this._detailFetchToken++;                  // invalidate any in-flight media fetch
  }

  /* ---- Detail media helpers ---- */

  // Insert Shopify CDN resize suffix before the extension. Images/posters only — never video URLs.
  _cdnResize(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?|$)/, `_${size}$1$2`);
  }

  // products.json images → unified media list (images only; this endpoint carries no video)
  _mediaFromImages(images) {
    return (images || []).map(img => {
      const src = typeof img === 'string' ? img : img?.src;
      if (!src) return null;
      return { type: 'image', full: this._cdnResize(src, '600x600'), thumb: this._cdnResize(src, '100x100') };
    }).filter(Boolean);
  }

  // /products/{handle}.js media array → unified media list, preserving product media order.
  // Native video only (media_type 'video'); external_video / model_3d are skipped.
  _mediaFromJs(mediaArr) {
    const list = [];
    for (const m of (mediaArr || [])) {
      if (m.media_type === 'image') {
        const src = m.src || m.preview_image?.src;
        if (!src) continue;
        list.push({ type: 'image', full: this._cdnResize(src, '600x600'), thumb: this._cdnResize(src, '100x100') });
      } else if (m.media_type === 'video') {
        const sources = m.sources || [];
        const mp4 = sources.find(s => (s.mime_type || '').indexOf('mp4') !== -1) || sources[0];
        if (!mp4 || !mp4.url) continue;
        const poster = m.preview_image?.src || '';
        list.push({
          type:  'video',
          src:   mp4.url,                                   // raw mp4 — NOT resized
          poster: poster ? this._cdnResize(poster, '600x600') : '',
          thumb:  poster ? this._cdnResize(poster, '100x100') : '',
        });
      }
    }
    return list;
  }

  _renderDetailThumbs() {
    const thumbsEl = this.detailDrawer.querySelector('.bundle-details-drawer__thumbs');
    thumbsEl.innerHTML = this._detailMedia.map((m, i) => {
      const active  = i === this._detailMediaIndex ? ' active' : '';
      const isVideo = m.type === 'video';
      const label   = isVideo ? `Video ${i + 1}` : `Afbeelding ${i + 1}`;
      const play    = isVideo ? '<span class="bundle-details-drawer__thumb-play" aria-hidden="true"></span>' : '';
      const thumbSrc = m.thumb || '';
      return `<button class="bundle-details-drawer__thumb${active}${isVideo ? ' is-video' : ''}" type="button" data-index="${i}" aria-label="${label}">
        <img src="${thumbSrc}" alt="" width="60" height="60" loading="lazy">${play}
      </button>`;
    }).join('');

    thumbsEl.querySelectorAll('.bundle-details-drawer__thumb').forEach(btn => {
      btn.addEventListener('click', () => this._showDetailMedia(parseInt(btn.dataset.index, 10)));
    });
  }

  _showDetailMedia(index) {
    const media = this._detailMedia;
    if (!media?.length || index < 0 || index >= media.length) return;

    const prevIndex = this._detailMediaIndex;
    this._detailMediaIndex = index;
    const item    = media[index];
    const dd      = this.detailDrawer;
    const wrap    = dd.querySelector('.bundle-details-drawer__main-wrap');
    const mainImg = wrap.querySelector('.bundle-details-drawer__main-img');

    // Always stop any playing video before switching the main display.
    this._pauseDetailVideo();

    // Decide whether to run the horizontal slide transition.
    // Only slides when moving between images (video targets swap instantly so the
    // native <video> controls aren't disturbed) and when the user hasn't asked to
    // reduce motion.
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dir = (prevIndex == null || prevIndex === index) ? 0 : (index > prevIndex ? 1 : -1);
    const animate = dir !== 0 && !reduceMotion;

    // Reset any transition still in flight from a previous rapid swipe.
    this._clearSlideLayer(wrap, mainImg);

    // Capture the outgoing frame as a temporary layer so the old image can be
    // seen sliding off-screen while the new one slides in.
    let outLayer = null;
    if (animate) {
      const prevItem = media[prevIndex];
      const outSrc = prevItem
        ? (prevItem.type === 'video' ? (prevItem.poster || prevItem.thumb) : prevItem.full)
        : '';
      if (outSrc) {
        outLayer = document.createElement('img');
        outLayer.className = 'bundle-details-drawer__slide-out';
        outLayer.src = outSrc;
        outLayer.alt = '';
        wrap.appendChild(outLayer);
        this._slideOut = outLayer;
      }
    }

    let incomingEl = null;
    if (item.type === 'video') {
      if (mainImg) mainImg.style.display = 'none';

      let video = wrap.querySelector('.bundle-details-drawer__main-video');
      if (!video) {
        video = document.createElement('video');
        video.className = 'bundle-details-drawer__main-video';
        video.setAttribute('playsinline', '');          // keep inline on iOS (no forced fullscreen)
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.preload  = 'metadata';
        wrap.appendChild(video);
      }
      video.controls = false;                            // no scrub bar / play-pause — behaves like an image
      video.loop     = true;                             // loop silently, like a GIF
      video.style.display = 'block';
      video.muted = true;                                // required for muted autoplay policy
      if (item.poster) video.poster = item.poster;
      if (video.dataset.src !== item.src) {              // avoid reloading the same source
        video.src = item.src;
        video.dataset.src = item.src;
        video.load();
      }
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(() => {/* autoplay blocked (e.g. iOS low-power) — poster remains */});
      }
      incomingEl = video;
    } else {
      const video = wrap.querySelector('.bundle-details-drawer__main-video');
      if (video) video.style.display = 'none';
      if (mainImg) {
        mainImg.style.display = '';                      // restore (CSS sets display:block)
        mainImg.src = item.full;
        mainImg.alt = this._detailProduct?.title || '';
      }
      incomingEl = mainImg;
    }

    // Run the slide: new image starts one panel-width off (in the swipe
    // direction) and animates to centre while the captured old frame slides out.
    if (animate && incomingEl && outLayer) {
      incomingEl.style.transition = 'none';
      incomingEl.style.transform  = `translateX(${dir * 100}%)`;
      outLayer.style.transition = 'none';
      outLayer.style.transform  = 'translateX(0)';
      void wrap.offsetWidth; // force reflow so the start positions take effect
      const ease = 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)';
      incomingEl.style.transition = ease;
      outLayer.style.transition   = ease;
      incomingEl.style.transform  = 'translateX(0)';
      outLayer.style.transform    = `translateX(${-dir * 100}%)`;

      const cleanup = () => {
        incomingEl.style.transition = '';
        incomingEl.style.transform  = '';
        if (outLayer.parentNode) outLayer.remove();
        if (this._slideOut === outLayer) this._slideOut = null;
      };
      outLayer.addEventListener('transitionend', cleanup, { once: true });
      clearTimeout(this._slideTimer);
      this._slideTimer = setTimeout(cleanup, 420); // fallback if transitionend never fires
    }

    dd.querySelectorAll('.bundle-details-drawer__thumb').forEach(btn => {
      btn.classList.toggle('active', parseInt(btn.dataset.index, 10) === index);
    });
    this._updateDetailControls(index);
  }

  /* ---- Swipe affordance: arrows, dots, first-time hint ---- */

  // Builds (once) and refreshes the arrows, pagination dots and the first-time
  // "swipe" hint inside the main image wrap. Elements live in the drawer, which
  // persists in <body>, so they are created lazily and reused across opens.
  _buildDetailControls() {
    const wrap = this.detailDrawer.querySelector('.bundle-details-drawer__main-wrap');
    if (!wrap) return;

    const count    = this._detailMedia?.length || 0;
    const multiple = count > 1;
    wrap.classList.toggle('has-multiple', multiple);

    // Prev / next arrows
    if (!wrap.querySelector('.bundle-details-drawer__nav--prev')) {
      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'bundle-details-drawer__nav bundle-details-drawer__nav--prev';
      prevBtn.setAttribute('aria-label', 'Vorige');
      prevBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      prevBtn.addEventListener('click', e => { e.stopPropagation(); this._showDetailMedia(this._detailMediaIndex - 1); });
      wrap.appendChild(prevBtn);

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'bundle-details-drawer__nav bundle-details-drawer__nav--next';
      nextBtn.setAttribute('aria-label', 'Volgende');
      nextBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      nextBtn.addEventListener('click', e => { e.stopPropagation(); this._showDetailMedia(this._detailMediaIndex + 1); });
      wrap.appendChild(nextBtn);
    }

    // Pagination dots
    let dots = wrap.querySelector('.bundle-details-drawer__dots');
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'bundle-details-drawer__dots';
      dots.setAttribute('aria-hidden', 'true');
      wrap.appendChild(dots);
    }
    dots.innerHTML = Array.from({ length: count }, (_, i) =>
      `<button type="button" class="bundle-details-drawer__dot" data-index="${i}" tabindex="-1" aria-hidden="true"></button>`
    ).join('');
    dots.querySelectorAll('.bundle-details-drawer__dot').forEach(dot => {
      dot.addEventListener('click', e => { e.stopPropagation(); this._showDetailMedia(parseInt(dot.dataset.index, 10)); });
    });

    // First-time swipe hint (touch devices only, shown once)
    if (!wrap.querySelector('.bundle-details-drawer__swipe-hint')) {
      const hint = document.createElement('div');
      hint.className = 'bundle-details-drawer__swipe-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = '<span class="bundle-details-drawer__swipe-hand" aria-hidden="true"></span><span>Veeg voor meer</span>';
      wrap.appendChild(hint);
    }
    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    let hintSeen = false;
    try { hintSeen = localStorage.getItem('betti_bundle_swipe_hint_seen') === '1'; } catch (e) {}
    if (multiple && isTouch && !hintSeen) {
      wrap.classList.add('show-swipe-hint');
      clearTimeout(this._swipeHintTimer);
      this._swipeHintTimer = setTimeout(() => this._hideSwipeHint(), 3500);
    } else {
      wrap.classList.remove('show-swipe-hint');
    }

    this._updateDetailControls(this._detailMediaIndex);
  }

  _updateDetailControls(index) {
    const wrap = this.detailDrawer?.querySelector('.bundle-details-drawer__main-wrap');
    if (!wrap) return;
    const count = this._detailMedia?.length || 0;
    wrap.querySelectorAll('.bundle-details-drawer__dot').forEach(dot => {
      dot.classList.toggle('active', parseInt(dot.dataset.index, 10) === index);
    });
    const prevBtn = wrap.querySelector('.bundle-details-drawer__nav--prev');
    const nextBtn = wrap.querySelector('.bundle-details-drawer__nav--next');
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index >= count - 1;
  }

  _hideSwipeHint() {
    const wrap = this.detailDrawer?.querySelector('.bundle-details-drawer__main-wrap');
    if (wrap) wrap.classList.remove('show-swipe-hint');
    clearTimeout(this._swipeHintTimer);
    try { localStorage.setItem('betti_bundle_swipe_hint_seen', '1'); } catch (e) {}
  }

  // Removes the temporary outgoing-image layer and resets any inline transform
  // left on the main image (used before starting a new slide and on close).
  _clearSlideLayer(wrap, mainImg) {
    clearTimeout(this._slideTimer);
    if (mainImg) { mainImg.style.transition = ''; mainImg.style.transform = ''; }
    if (wrap) {
      wrap.querySelectorAll('.bundle-details-drawer__slide-out').forEach(el => el.remove());
      const v = wrap.querySelector('.bundle-details-drawer__main-video');
      if (v) { v.style.transition = ''; v.style.transform = ''; }
    }
    this._slideOut = null;
  }

  _pauseDetailVideo() {
    const video = this.detailDrawer.querySelector('.bundle-details-drawer__main-video');
    if (video) { try { video.pause(); } catch (e) {} }
  }

  _teardownDetailVideo() {
    const video = this.detailDrawer.querySelector('.bundle-details-drawer__main-video');
    if (!video) return;
    try {
      video.pause();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      delete video.dataset.src;
      video.load();                                      // releases the buffer / stops download
    } catch (e) {}
    video.style.display = 'none';
  }

  _updateConfirmButton() {
    const confirmBtn = this.drawer.querySelector('.bundle-drawer__confirm');
    const statusEl   = this.drawer.querySelector('.bundle-drawer__status');
    confirmBtn.disabled = !this.tempSelection;
    statusEl.textContent = this.tempSelection ? `Geselecteerd: ${this.tempSelection.title}` : '';
  }

  /* ---- Slot state ---- */

  _setSlot(index, product) {
    this.slots[index] = product;

    const slotEl    = this.querySelector(`[data-slot="${index}"]`);
    const emptyEl   = slotEl.querySelector('.bundle-picker__slot-empty');
    const filledEl  = slotEl.querySelector('.bundle-picker__slot-filled');
    const imgEl     = slotEl.querySelector('.bundle-picker__slot-img');
    const nameEl    = slotEl.querySelector('.bundle-picker__slot-name');

    emptyEl.hidden  = true;
    filledEl.hidden = false;
    slotEl.dataset.filled = 'true';

    imgEl.src = product.image;
    imgEl.alt = product.title;
    nameEl.textContent = product.title;

    this._updateSummary();
  }

  _clearSlot(index) {
    this.slots[index] = null;

    const slotEl   = this.querySelector(`[data-slot="${index}"]`);
    const emptyEl  = slotEl.querySelector('.bundle-picker__slot-empty');
    const filledEl = slotEl.querySelector('.bundle-picker__slot-filled');

    emptyEl.hidden  = false;
    filledEl.hidden = true;
    delete slotEl.dataset.filled;

    this._updateSummary();
  }

  /* ---- UI summary ---- */

  _updateSummary() {
    const filledCount = this.slots.filter(Boolean).length;
    const allFilled   = filledCount === this.slots.length;

    // Add-to-cart button
    const atcBtn = this.querySelector('.bundle-picker__atc');
    atcBtn.disabled = !allFilled;

    this._updateOriginalPrice(allFilled);
  }

  /**
   * Keeps the struck-through "was" price honest.
   *
   * Liquid renders a baseline derived from the cheapest qualifying products.
   * Once every slot is filled we know exactly what the customer chose, so we
   * replace it with the real sum. If that sum isn't above the bundle price
   * (all-sale cart, misconfiguration) the strikethrough is hidden rather than
   * showing a "discount" that isn't one.
   */
  _updateOriginalPrice(allFilled) {
    const el = this.querySelector('.bundle-picker__price-original');
    if (!el) return;

    if (!allFilled) {
      // Restore the server-rendered baseline while the selection is incomplete.
      const baseline = this._bundleOriginalPrice;
      el.textContent = baseline;
      el.hidden = !baseline || this._parseMoney(baseline) <= this._bundlePriceCents;
      return;
    }

    const totalCents = this.slots.reduce(
      (sum, p) => sum + Math.round((p?.price || 0) * 100),
      0
    );

    if (totalCents > this._bundlePriceCents) {
      el.textContent = this._formatMoney(totalCents);
      el.hidden = false;
      this._bundleOriginalPrice = el.textContent; // stamped onto the cart line
    } else {
      el.hidden = true;
      this._bundleOriginalPrice = '';
    }
  }

  /** "€57,85" -> 5785 */
  _parseMoney(str) {
    const digits = (str || '').replace(/[^\d]/g, '');
    return digits ? parseInt(digits, 10) : 0;
  }

  /** 5785 -> "€57,85" (nl-NL formatting, matching the rest of the shop) */
  _formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /* ---- Add to cart ---- */

  async _addToCart() {
    if (!this.slots.every(Boolean)) return;

    // A slot with a missing/NaN variant id would post `id: null` and fail
    // opaquely. Catch it here instead.
    if (!this.slots.every(p => Number.isInteger(p.variantId) && p.variantId > 0)) {
      console.error('[BundlePicker] Invalid variant in selection', this.slots);
      const btn = this.querySelector('.bundle-picker__atc');
      btn.textContent = 'Fout – herlaad de pagina';
      setTimeout(() => { btn.textContent = 'Voeg bundel toe aan winkelwagen'; }, 3000);
      return;
    }

    const bundleId = `bundle_${Date.now()}`;
    const atcBtn   = this.querySelector('.bundle-picker__atc');

    atcBtn.disabled    = true;
    atcBtn.textContent = 'Toevoegen…';

    const items = this.slots.map((product, index) => ({
      id:         product.variantId,
      quantity:   1,
      properties: {
        _bundle_id:             bundleId,
        _bundle_slot:           String(index),
        _bundle_name:           this._bundleName,
        _bundle_price:          this._bundlePrice,
        _bundle_original_price: this._bundleOriginalPrice,
      },
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Cart error');
      }

      // Savor listens for 'cart:update' and needs sections HTML to re-render the drawer.
      // Read the section ID from the cart-items-component, fetch re-rendered HTML via
      // Shopify's sections API, then dispatch the event Savor actually listens for.
      const cartItemsEl = document.querySelector('cart-items-component[data-section-id]');
      const sectionId   = cartItemsEl ? cartItemsEl.dataset.sectionId : null;

      if (sectionId) {
        const sectionsRes  = await fetch(`/?sections=${encodeURIComponent(sectionId)}`);
        const sectionsData = await sectionsRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: null,
            data: { sections: sectionsData },
          },
        }));
      }

      // Open the cart drawer
      const drawerComponent = document.querySelector('cart-drawer-component');
      if (drawerComponent && typeof drawerComponent.open === 'function') {
        drawerComponent.open();
      } else if (drawerComponent && typeof drawerComponent.showDialog === 'function') {
        drawerComponent.showDialog();
      }

      atcBtn.textContent = '✓ Toegevoegd!';
      setTimeout(() => {
        atcBtn.disabled    = false;
        atcBtn.textContent = 'Voeg bundel toe aan winkelwagen';
      }, 2500);

    } catch (err) {
      console.error('[BundlePicker] Add to cart failed:', err);
      atcBtn.disabled    = false;
      atcBtn.textContent = 'Fout – probeer opnieuw';
      setTimeout(() => {
        atcBtn.textContent = 'Voeg bundel toe aan winkelwagen';
      }, 3000);
    }
  }
}

customElements.define('bundle-picker-component', BundlePickerComponent);
